'use client';

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

type ToastKind = 'info' | 'error' | 'success';
interface ToastItem { id: string; msg: string; kind: ToastKind }

interface ToastApi {
  show: (msg: string, kind?: ToastKind) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const show = useCallback((msg: string, kind: ToastKind = 'info') => {
    const id = Math.random().toString(36).slice(2);
    setItems((prev) => [...prev, { id, msg, kind }]);
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), 3500);
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div
        style={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {items.map((t) => (
          <div
            key={t.id}
            style={{
              background: t.kind === 'error' ? 'var(--accent)' : 'var(--ink)',
              color: 'var(--paper)',
              padding: '12px 18px',
              borderRadius: 2,
              fontSize: 14,
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
              maxWidth: 460,
              animation: 'fadeIn 200ms ease both',
            }}
          >
            {t.msg}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Allow components to call useToast without provider — silent no-op
    return { show: () => undefined };
  }
  return ctx;
}

// Re-mount safe: support legacy callers that read from a fixed global
// (kept for backward-compat with prototype components).
export function useEnsureGlobalToast() {
  const { show } = useToast();
  useEffect(() => {
    (window as typeof window & { __toast?: (m: string, k?: ToastKind) => void }).__toast = show;
  }, [show]);
}
